import CUSTOMER_PAYROLL_STATUS from "constants/customer.constants";

// Labels match the payment history chips and the Update Payment Status modal,
// so billing service statuses (un-paid draft, finalized, void) read the same
// everywhere instead of falling through to "-".
const PAYMENT_STATUS_LABELS = {
  paid: "Paid",
  "un-paid": "Draft",
  draft: "Draft",
  pending: "Processing",
  processing: "Processing",
  finalized: "Processing",
  failed: "Failed",
  payment_failed: "Failed",
  void: "Void",
};

const PAYMENT_STATUS_COLORS = {
  Paid: "var(--color-text-success)",
  Draft: "var(--color-text-warning)",
  Processing: "var(--color-text-warning)",
  Failed: "var(--color-text-danger)",
  Void: "var(--color-text-danger)",
};

const getPaymentStatusLabel = (status) => PAYMENT_STATUS_LABELS[String(status || "").toLowerCase()];

export const getPaymentStatusText = (navigatedFrom, paymentDetailStatus) => {
  if (navigatedFrom == "paymentDetails") {
    return getPaymentStatusLabel(paymentDetailStatus) || "-";
  }
  return "-";
};

export const getStatusColor = (status) =>
  PAYMENT_STATUS_COLORS[getPaymentStatusLabel(status)] || "var(--color-primitives-primary)";

export const getPaymentChipColor = (status) => {
  if (status === "failed") {
    return "error";
  }
  if (status === "pending") {
    return "warning";
  }
  if (status === "un-paid") {
    return "warning";
  }
  // [PRD-3348] A voided invoice was never charged; show it red like the
  // billing dashboard's void chip, not green.
  if (status === CUSTOMER_PAYROLL_STATUS.VOID) {
    return "error";
  }
  return "success";
};

export const getPaymentChipLabel = (status) => {
  if (status === "failed") {
    return "Failed";
  }
  if (status === "pending") {
    return "Pending";
  }
  if (status === "un-paid") {
    return "Pending";
  }
  if (status === CUSTOMER_PAYROLL_STATUS.VOID) {
    return "Void";
  }
  return "Successful";
};

export const getPaymentChipIconColor = (status) => {
  if (status === "success") {
    return "color-chip-icon-success";
  }
  if (status === "pending" || status === "un-paid") {
    return "color-chip-icon-warning";
  }
  return "color-chip-icon-error";
};

export const getDaysUntilExpiry = (expiry) => {
  if (!expiry || typeof expiry !== "string" || !expiry.includes("-")) {
    return { diffDays: null, expiryDate: null };
  }

  const [month, year] = expiry.split("-").map(Number);

  if (!month || !year) {
    return { diffDays: null, expiryDate: null };
  }

  const expiryDate = new Date(year, month, 0);

  if (Number.isNaN(expiryDate.getTime())) {
    return { diffDays: null, expiryDate: null };
  }

  const today = new Date();
  const diffTime = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return { diffDays, expiryDate };
};

export function CardExpiryWarning({ expiry, warningDays }) {
  const result = getDaysUntilExpiry(expiry);

  if (!result || result.diffDays === null || !result.expiryDate) {
    return null;
  }

  const { diffDays, expiryDate } = result;

  if (diffDays <= warningDays) {
    if (diffDays < 0)
      return (
        <p className="heading-h5-medium color-text-danger">
          Expired on{" "}
          {expiryDate.toLocaleDateString("en-US", {
            month: "2-digit",
            year: "2-digit",
          })}
        </p>
      );
    if (diffDays === 0) return <p className="heading-h5-medium color-text-warning-secondary">Expires today</p>;
    return (
      <p className="heading-h5-medium color-text-warning-secondary">
        Expires in {diffDays} days on{" "}
        {expiryDate.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })}
      </p>
    );
  }

  return (
    <p className="heading-h5-medium color-text-body-primary">
      Exp. date{" "}
      {expiryDate.toLocaleDateString("en-US", {
        month: "2-digit",
        year: "2-digit",
      })}
    </p>
  );
}
